// src/pages/PropertiesAdvancedPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://mrm.lemda.lk/api';
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_STATIC_KEY || ''; // optional

// Inline SVG placeholder for broken/missing images
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
    <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#e5e7eb"/><stop offset="100%" stop-color="#cbd5e1"/></linearGradient></defs>
    <rect width="1200" height="800" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="600" y="390" font-size="28" font-weight="700">Property image</text>
      <text x="600" y="425" font-size="14" opacity="0.85">Not available</text>
    </g>
  </svg>`);

// helpers
const fmtGBP = (v, text) =>
  text
    ? text
    : v == null
    ? 'POA'
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
      }).format(v);

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const parseNumber = (v) => (v == null || v === '' ? undefined : Number(v));

function buildQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return;
    if (Array.isArray(v)) q.set(k, v.join(','));
    else q.set(k, String(v));
  });
  return q.toString();
}

function useDebouncedEffect(effect, deps, delay = 400) {
  const clean = useRef();
  useEffect(() => {
    const h = setTimeout(() => {
      if (clean.current) clean.current();
      clean.current = effect();
    }, delay);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default function PropertiesAdvancedPage() {
  // ---------- STATE ----------
  const [filters, setFilters] = useState(() => {
    // Seed from URL
    const sp = new URLSearchParams(window.location.search);
    const types = sp.get('types') ? sp.get('types').split(',') : ['sale', 'rent', 'auction'];
    return {
      q: sp.get('q') || '',
      towns: sp.get('towns') ? sp.get('towns').split(',') : [],
      min_price: parseNumber(sp.get('min_price')),
      max_price: parseNumber(sp.get('max_price')),
      beds: parseNumber(sp.get('beds')),
      baths: parseNumber(sp.get('baths')),
      types,
      sort: sp.get('sort') || 'newest', // newest|price_asc|price_desc|beds_desc
      view: sp.get('view') || 'grid', // grid|list|map
      page: clamp(parseInt(sp.get('page') || '1', 10) || 1, 1, 9999),
      per_page: clamp(parseInt(sp.get('per_page') || '12', 10) || 12, 6, 60),
    };
  });

  const [facets, setFacets] = useState({
    towns: [],
    price_min: 0,
    price_max: 1000000,
    beds_max: 6,
    baths_max: 4,
  });
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false); // mobile filters

  const appliedCount = useMemo(() => {
    let n = 0;
    const def = { types: ['sale', 'rent', 'auction'] };
    if (filters.q) n++;
    if (filters.towns.length) n++;
    if (filters.min_price != null) n++;
    if (filters.max_price != null) n++;
    if (filters.beds != null) n++;
    if (filters.baths != null) n++;
    if (JSON.stringify([...filters.types].sort()) !== JSON.stringify([...def.types].sort())) n++;
    return n;
  }, [filters]);

  // ---------- SYNC URL ----------
  useEffect(() => {
    const qs = buildQuery(filters);
    const url = `${window.location.pathname}?${qs}`;
    window.history.replaceState(null, '', url);
  }, [filters]);

  // ---------- FETCH FACETS (once) ----------
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/properties_facets.php`);
        const json = await res.json().catch(() => ({}));
        if (!ignore && json?.ok) {
          setFacets({
            towns: json.data.towns || [],
            price_min: json.data.price_min ?? 0,
            price_max: json.data.price_max ?? 1000000,
            beds_max: json.data.beds_max ?? 6,
            baths_max: json.data.baths_max ?? 4,
          });
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  // ---------- FETCH LIST (debounced on filters) ----------
  useDebouncedEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const qs = buildQuery({
          q: filters.q,
          towns: filters.towns,
          min_price: filters.min_price,
          max_price: filters.max_price,
          beds: filters.beds,
          baths: filters.baths,
          types: filters.types,
          sort: filters.sort,
          page: filters.page,
          per_page: filters.per_page,
          status: 'active',
        });
        const res = await fetch(`${API_BASE}/properties.php?${qs}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || 'Failed to load');
        if (cancel) return;
        setData({
          items: json.data || [],
          total: json.total || 0,
          total_pages: json.total_pages || 0,
        });
      } catch (e) {
        if (!cancel) setErr(e.message || 'Network error');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [filters], 450);

  // ---------- HANDLERS ----------
  const set = (patch) => setFilters((f) => ({ ...f, ...patch, page: 1 })); // reset page on most changes
  const toggleType = (t) => {
    setFilters((f) => {
      const on = new Set(f.types);
      on.has(t) ? on.delete(t) : on.add(t);
      if (on.size === 0) on.add(t); // keep at least one
      return { ...f, types: Array.from(on), page: 1 };
    });
  };
  const toggleTown = (t) => {
    setFilters((f) => {
      const on = new Set(f.towns);
      on.has(t) ? on.delete(t) : on.add(t);
      return { ...f, towns: Array.from(on), page: 1 };
    });
  };

  const currentPage = filters.page;
  const totalPages = Math.max(1, data.total_pages || 1);
  const goPage = (p) => setFilters((f) => ({ ...f, page: clamp(p, 1, totalPages) }));
  const clearAll = () =>
    setFilters({
      q: '',
      towns: [],
      min_price: '',
      max_price: '',
      beds: '',
      baths: '',
      types: ['sale', 'rent', 'auction'],
      sort: 'newest',
      view: filters.view,
      page: 1,
      per_page: 12,
    });

  // markers for map view (limit to avoid URL length)
  const mapMarkers = useMemo(() => {
    return (data.items || [])
      .filter((p) => p.lat != null && p.lng != null)
      .slice(0, 25)
      .map((p) => `${p.lat},${p.lng}`);
  }, [data.items]);

  const mapCenter = useMemo(() => {
    if (!mapMarkers.length) return 'Middlesbrough';
    const coords = mapMarkers.map((m) => m.split(',').map(Number));
    const avgLat = coords.reduce((a, c) => a + c[0], 0) / coords.length;
    const avgLng = coords.reduce((a, c) => a + c[1], 0) / coords.length;
    return `${avgLat},${avgLng}`;
  }, [mapMarkers]);

  return (
    <>
      <style>{`
        /* ===== Advanced Properties (namespaced) ===== */
        .mrm-adv{ --b:#1e3a8a; --b2:#0ea5e9; --ink:#0f172a; --muted:#475569; --bg:#f8fafc; --paper:#fff; --line:#e5e7eb; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:var(--ink);}
        .mrm-adv__wrap{ max-width: 1200px; margin: 0 auto; padding: .75rem 1rem 1rem; }
        .mrm-adv__head{ position:sticky; top:0; z-index:5; background: linear-gradient(#ffffff, #ffffffcc); backdrop-filter: blur(6px); border-bottom:1px solid var(--line);}
        .mrm-adv__headin{ max-width:1200px; margin:0 auto; padding:.6rem 1rem; display:flex; align-items:center; justify-content:space-between; gap:.8rem; flex-wrap:wrap; }
        .mrm-adv__title{ margin:0; font-weight:900; font-size: clamp(1.15rem, 2.4vw, 1.6rem); color:var(--ink); }
        .mrm-adv__meta{ color:var(--muted); font-size:.9rem; }
        .mrm-adv__right{ display:flex; gap:.5rem; align-items:center; }

        .mrm-adv__filterbtn{ border:1px solid var(--line); background:#fff; border-radius:999px; padding:.55rem .9rem; font-weight:800; cursor:pointer; }
        .mrm-adv__select{ border:1px solid var(--line); background:#fff; border-radius:999px; padding:.5rem .8rem; min-width:160px; font-weight:700; }

        /* Inline filters (desktop) */
        .mrm-adv__filters{ background:var(--paper); border:1px solid var(--line); border-radius: 14px; box-shadow:0 10px 22px rgba(2,6,23,.06); padding:.8rem; display:grid; gap:.8rem; margin-top:.8rem; }
        .mrm-adv__row{ display:grid; grid-template-columns: 1fr; gap:.8rem; }
        @media (min-width: 980px){ .mrm-adv__row{ grid-template-columns: 3fr 2fr 2fr 2fr 2fr; } }

        .mrm-adv__field{ display:flex; flex-direction:column; gap:.35rem; }
        .mrm-adv__label{ font-size:.82rem; color:#334155; font-weight:800; }
        .mrm-adv__input, .mrm-adv__selectin{
          width:100%; border:1px solid #cbd5e1; border-radius:10px; padding:.65rem .8rem; font-size:.95rem; outline:none;
          transition: border-color .2s, box-shadow .2s;
        }
        .mrm-adv__input:focus, .mrm-adv__selectin:focus{ border-color: var(--b); box-shadow: 0 0 0 3px rgba(30,58,138,.15); }

        .mrm-adv__chips{ display:flex; gap:.4rem; flex-wrap:wrap; }
        .mrm-adv__chip{
          display:inline-flex; align-items:center; gap:.35rem; padding:.45rem .6rem; border-radius:999px; border:1px solid var(--line); background:#f1f5f9; font-weight:800; cursor:pointer; user-select:none;
        }
        .mrm-adv__chip--on{ background:#eef2ff; border-color:#c7d2fe; color:#1e3a8a; }

        .mrm-adv__toolbar{ display:flex; gap:.6rem; flex-wrap:wrap; align-items:center; justify-content:space-between; margin-top:.4rem; }
        .mrm-adv__left{ display:flex; gap:.6rem; align-items:center; flex-wrap:wrap; }
        .mrm-adv__clear{ border:1px solid var(--line); background:#fff; border-radius:8px; padding:.5rem .7rem; font-weight:800; cursor:pointer; }
        .mrm-adv__view{ display:flex; gap:.35rem; background:#f1f5f9; border:1px solid var(--line); border-radius:999px; padding:.25rem; }
        .mrm-adv__viewbtn{
          border:none; background:transparent; padding:.45rem .65rem; border-radius:999px; cursor:pointer; font-weight:800; color:#334155;
        }
        .mrm-adv__viewbtn[aria-pressed="true"]{ background:#fff; box-shadow:0 6px 16px rgba(2,6,23,.08); color:#1e3a8a; border:1px solid var(--line); }

        /* Mobile filter sheet */
        .mrm-adv__sheet{ position: fixed; inset:0; z-index: 30; display:none; }
        .mrm-adv__sheet[data-open="true"]{ display:block; }
        .mrm-adv__overlay{ position:absolute; inset:0; background:rgba(2,6,23,.5); }
        .mrm-adv__panel{ position:absolute; bottom:0; left:0; right:0; background:#fff; border-top-left-radius:16px; border-top-right-radius:16px; max-height:80vh; overflow:auto; padding:1rem; }
        .mrm-adv__panel header{ display:flex; align-items:center; justify-content:space-between; gap:.5rem; margin-bottom:.4rem; }
        .mrm-adv__sheet-close{ border:none; background:#f1f5f9; padding:.5rem .7rem; border-radius:8px; font-weight:800; cursor:pointer; }
        .mrm-adv__sheet-apply{ position:sticky; bottom:0; background:#fff; padding:.6rem 0 .2rem; display:grid; grid-template-columns: 1fr 1fr; gap:.5rem; }
        .mrm-adv__btn{ border:1px solid var(--line); background:#fff; border-radius:10px; padding:.7rem 1rem; font-weight:900; cursor:pointer; }
        .mrm-adv__btn--primary{ background: var(--b2); color:#fff; border-color: var(--b2); }

        /* Results */
        .mrm-adv__grid{ display:grid; grid-template-columns: 1fr; gap: .9rem; margin-top: 1rem; }
        @media (min-width: 760px){ .mrm-adv__grid{ grid-template-columns: 1fr 1fr; } }
        @media (min-width: 1100px){ .mrm-adv__grid{ grid-template-columns: 1fr 1fr 1fr; } }
        .mrm-adv__card{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; display:flex; flex-direction:column; }
        .mrm-adv__thumb{ position:relative; height:180px; background:#e5e7eb; }
        .mrm-adv__thumb img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
        .mrm-adv__pill{ position:absolute; top:10px; left:10px; padding:.25rem .5rem; border-radius:999px; background:rgba(0,0,0,.6); color:#fff; font-weight:800; font-size:.75rem; }
        .mrm-adv__body{ padding:.8rem .9rem; display:grid; gap:.35rem; }
        .mrm-adv__h{ margin:0; font-weight:800; font-size:1rem; color:var(--ink); display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:2.6em; }
        .mrm-adv__meta{ color:#64748b; font-size:.9rem; }
        .mrm-adv__price{ color:#b91c1c; font-weight:900; }
        .mrm-adv__specs{ color:#475569; font-size:.9rem; }

        /* List view */
        .mrm-adv__list{ display:grid; gap:.9rem; margin-top:1rem; }
        .mrm-adv__rowcard{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; display:grid; grid-template-columns: 180px 1fr; }
        @media (max-width: 640px){ .mrm-adv__rowcard{ grid-template-columns: 1fr; } }
        .mrm-adv__rowthumb{ height:180px; background:#e5e7eb; position:relative; }
        .mrm-adv__rowthumb img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
        .mrm-adv__rowbody{ padding:.8rem .9rem; display:grid; gap:.35rem; }

        /* Map view */
        .mrm-adv__mapwrap{ margin-top:1rem; border:1px solid var(--line); border-radius:12px; overflow:hidden; background:#fff; }
        .mrm-adv__mapimg{ width:100%; height:420px; object-fit:cover; display:block; }

        /* Pagination */
        .mrm-adv__pager{ display:flex; gap:.35rem; justify-content:center; align-items:center; margin: 1rem 0; flex-wrap:wrap; }
        .mrm-adv__pagebtn{ border:1px solid var(--line); background:#fff; color:#334155; border-radius:8px; padding:.45rem .65rem; cursor:pointer; font-weight:800; }
        .mrm-adv__pagebtn[aria-current="page"]{ background:#1e3a8a; color:#fff; border-color:#1e3a8a; }

        /* Skeletons */
        @keyframes shimmer { 0%{background-position:-450px 0} 100%{background-position:450px 0} }
        .mrm-adv__sk{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; }
        .mrm-adv__skrect{ height:180px; background:linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%); background-size:900px 100%; animation:shimmer 1.2s infinite; }
        .mrm-adv__skline{ height:12px; margin:.6rem; border-radius:6px; background:linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%); background-size:900px 100%; animation:shimmer 1.2s infinite; }
      `}</style>

      <main className="mrm-adv">
        {/* Sticky head with mobile controls */}
        <div className="mrm-adv__head">
          <div className="mrm-adv__headin">
            <div>
              <h1 className="mrm-adv__title">Find your next property</h1>
              <div className="mrm-adv__meta">{data.total.toLocaleString()} results</div>
            </div>

            <div className="mrm-adv__right">
              {/* Mobile filter opener */}
              <button
                type="button"
                className="mrm-adv__filterbtn"
                onClick={() => setSheetOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={sheetOpen}
                aria-controls="filter-sheet"
              >
                🔎 Filters {appliedCount ? `(${appliedCount})` : ''}
              </button>

              {/* Quick sort + view */}
              <select
                className="mrm-adv__select"
                value={filters.sort}
                onChange={(e) => set({ sort: e.target.value })}
                aria-label="Sort results"
              >
                <option value="newest">Newest</option>
                <option value="price_asc">Price (low–high)</option>
                <option value="price_desc">Price (high–low)</option>
                <option value="beds_desc">Beds (most)</option>
              </select>

              <div className="mrm-adv__view" role="tablist" aria-label="Change view">
                {['grid', 'list'].map((v) => (
                  <button
                    key={v}
                    className="mrm-adv__viewbtn"
                    type="button"
                    aria-pressed={filters.view === v}
                    onClick={() => setFilters((f) => ({ ...f, view: v }))}
                  >
                    {v.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mrm-adv__wrap">
          {/* Desktop filters */}
        

          {/* Mobile sheet */}
          <div
            id="filter-sheet"
            className="mrm-adv__sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-title"
            data-open={sheetOpen}
          >
            <div className="mrm-adv__overlay" onClick={() => setSheetOpen(false)} />
            <div className="mrm-adv__panel">
              <header>
                <h3 id="filter-title" style={{ margin: 0, fontWeight: 900 }}>Filters</h3>
                <button className="mrm-adv__sheet-close" onClick={() => setSheetOpen(false)}>Close ✕</button>
              </header>

              {/* Compact mobile fields */}
              <div className="mrm-adv__field">
                <label className="mrm-adv__label" htmlFor="mq">Search</label>
                <input id="mq" className="mrm-adv__input" placeholder="Keyword…" value={filters.q} onChange={(e)=>set({ q: e.target.value })} />
              </div>

              <div className="mrm-adv__field">
                <label className="mrm-adv__label">Listing type</label>
                <div className="mrm-adv__chips">
                  {['sale','rent','auction'].map((t)=>(
                    <button key={t} className={`mrm-adv__chip ${filters.types.includes(t) ? 'mrm-adv__chip--on':''}`} onClick={()=>toggleType(t)}>{t.toUpperCase()}</button>
                  ))}
                </div>
              </div>

              <div className="mrm-adv__field">
                <label className="mrm-adv__label">Towns</label>
                <div className="mrm-adv__chips" style={{maxHeight:'124px', overflow:'auto'}}>
                  {facets.towns.map((t)=>(
                    <button key={t} className={`mrm-adv__chip ${filters.towns.includes(t) ? 'mrm-adv__chip--on':''}`} onClick={()=>toggleTown(t)}>{t}</button>
                  ))}
                </div>
              </div>

              <div className="mrm-adv__field">
                <label className="mrm-adv__label">Price range</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.5rem' }}>
                  <input className="mrm-adv__input" inputMode="numeric" placeholder="Min"
                    value={filters.min_price ?? ''} onChange={(e)=>set({ min_price: e.target.value.replace(/\D/g,'') || '' })} />
                  <input className="mrm-adv__input" inputMode="numeric" placeholder="Max"
                    value={filters.max_price ?? ''} onChange={(e)=>set({ max_price: e.target.value.replace(/\D/g,'') || '' })} />
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.5rem' }}>
                <div className="mrm-adv__field">
                  <label className="mrm-adv__label">Beds</label>
                  <select className="mrm-adv__selectin" value={filters.beds ?? ''} onChange={(e)=>set({ beds: e.target.value? Number(e.target.value): '' })}>
                    <option value="">Any</option>
                    {Array.from({length: facets.beds_max || 6}).map((_,i)=>(<option key={i+1} value={i+1}>{i+1}+</option>))}
                  </select>
                </div>
                <div className="mrm-adv__field">
                  <label className="mrm-adv__label">Baths</label>
                  <select className="mrm-adv__selectin" value={filters.baths ?? ''} onChange={(e)=>set({ baths: e.target.value? Number(e.target.value): '' })}>
                    <option value="">Any</option>
                    {Array.from({length: facets.baths_max || 4}).map((_,i)=>(<option key={i+1} value={i+1}>{i+1}+</option>))}
                  </select>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.5rem', marginTop:'.5rem' }}>
                <div className="mrm-adv__field">
                  <label className="mrm-adv__label">Sort</label>
                  <select className="mrm-adv__selectin" value={filters.sort} onChange={(e)=>set({ sort: e.target.value })}>
                    <option value="newest">Newest</option>
                    <option value="price_asc">Price (low–high)</option>
                    <option value="price_desc">Price (high–low)</option>
                    <option value="beds_desc">Beds (most)</option>
                  </select>
                </div>
                <div className="mrm-adv__field">
                  <label className="mrm-adv__label">View</label>
                  <select className="mrm-adv__selectin" value={filters.view} onChange={(e)=>setFilters((f)=>({ ...f, view:e.target.value }))}>
                    <option value="grid">Grid</option>
                    <option value="list">List</option>
                    <option value="map">Map</option>
                  </select>
                </div>
              </div>

              <div className="mrm-adv__sheet-apply">
                <button className="mrm-adv__btn" onClick={clearAll}>Clear all</button>
                <button className="mrm-adv__btn mrm-adv__btn--primary" onClick={()=>setSheetOpen(false)}>Apply filters</button>
              </div>
            </div>
          </div>

          {/* Errors */}
          {err && <div style={{ color: '#991b1b', fontWeight: 900, marginTop: '1rem' }}>{err}</div>}

          {/* RESULTS */}
          {!loading && filters.view === 'grid' && (
            <section className="mrm-adv__grid">
              {data.items.map((p) => {
                const img = p.full_image_url || p.image_url || PLACEHOLDER;
                const href = p.href || (p.slug ? `/property/${p.slug}` : '#');
                const label = (p.listing_type || '').toUpperCase();
                const meta = [p.town, p.postcode].filter(Boolean).join(' · ');
                return (
                  <a key={p.id} className="mrm-adv__card" href={href}>
                    <div className="mrm-adv__thumb">
                      <img
                        src={img}
                        alt={p.title}
                        loading="lazy"
                        onError={(e) => {
                          if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER;
                        }}
                      />
                      {label && <span className="mrm-adv__pill">{label}</span>}
                    </div>
                    <div className="mrm-adv__body">
                      <h3 className="mrm-adv__h">{p.title}</h3>
                      <div className="mrm-adv__meta">{meta}</div>
                      <div className="mrm-adv__price">{fmtGBP(p.price, p.price_text)}</div>
                      <div className="mrm-adv__specs">🛏 {p.beds ?? 0} · 🛁 {p.baths ?? 0}</div>
                    </div>
                  </a>
                );
              })}
            </section>
          )}

          {!loading && filters.view === 'list' && (
            <section className="mrm-adv__list">
              {data.items.map((p) => {
                const img = p.full_image_url || p.image_url || PLACEHOLDER;
                const href = p.href || (p.slug ? `/property/${p.slug}` : '#');
                const meta = [p.town, p.postcode].filter(Boolean).join(' · ');
                return (
                  <a key={p.id} className="mrm-adv__rowcard" href={href}>
                    <div className="mrm-adv__rowthumb">
                      <img
                        src={img}
                        alt={p.title}
                        loading="lazy"
                        onError={(e) => {
                          if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER;
                        }}
                      />
                      {p.listing_type && (
                        <span className="mrm-adv__pill">{String(p.listing_type).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="mrm-adv__rowbody">
                      <h3 className="mrm-adv__h">{p.title}</h3>
                      <div className="mrm-adv__meta">{meta}</div>
                      <div className="mrm-adv__price">{fmtGBP(p.price, p.price_text)}</div>
                      <div className="mrm-adv__specs">🛏 {p.beds ?? 0} · 🛁 {p.baths ?? 0}</div>
                    </div>
                  </a>
                );
              })}
            </section>
          )}

          {!loading && filters.view === 'map' && (
            <section className="mrm-adv__mapwrap" aria-label="Map view">
              {GMAPS_KEY ? (
                <img
                  className="mrm-adv__mapimg"
                  src={`https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(
                    mapCenter
                  )}&zoom=11&size=1200x420&maptype=roadmap${
                    mapMarkers.map((m) => `&markers=color:red%7C${encodeURIComponent(m)}`).join('')
                  }&key=${GMAPS_KEY}`}
                  alt="Map preview"
                  onError={(e) => {
                    e.currentTarget.style.opacity = '0.6';
                  }}
                  loading="lazy"
                />
              ) : (
                <div
                  className="mrm-adv__mapimg"
                  role="img"
                  aria-label="Map placeholder. Provide REACT_APP_GOOGLE_MAPS_STATIC_KEY to enable."
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    background:
                      'repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb 12px,#f3f4f6 12px,#f3f4f6 24px)',
                    color: '#475569',
                    fontWeight: 800,
                  }}
                >
                  Enable static maps by setting <code>REACT_APP_GOOGLE_MAPS_STATIC_KEY</code>
                </div>
              )}
            </section>
          )}

          {/* Loading skeletons */}
          {loading && (
            <section className="mrm-adv__grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="mrm-adv__sk">
                  <div className="mrm-adv__skrect" />
                  <div className="mrm-adv__skline" style={{ width: '80%' }} />
                  <div className="mrm-adv__skline" style={{ width: '60%' }} />
                  <div className="mrm-adv__skline" style={{ width: '40%' }} />
                </div>
              ))}
            </section>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <nav className="mrm-adv__pager" aria-label="Pagination">
              <button
                className="mrm-adv__pagebtn"
                onClick={() => goPage(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }).slice(0, 7).map((_, i) => {
                const p = i + 1; // show first 7 pages (simple window)
                return (
                  <button
                    key={p}
                    className="mrm-adv__pagebtn"
                    onClick={() => goPage(p)}
                    aria-current={p === currentPage ? 'page' : undefined}
                  >
                    {p}
                  </button>
                );
              })}
              {totalPages > 7 && <span className="mrm-adv__meta">…</span>}
              <button
                className="mrm-adv__pagebtn"
                onClick={() => goPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </main>
    </>
  );
}
